'use client';

import { Button } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import { DEFAULT_THROTTLE_SECONDS, useThrottledResend } from './useThrottledResend';

export { DEFAULT_THROTTLE_SECONDS };

export type ThrottledResendProps = {
  /** Called only when the customer presses the control, and only once the countdown has reached
   *  zero. Never called while disabled. */
  onResend: () => void;
  /** The button's words once the countdown has expired. Caller-supplied, no default: this
   *  component authors no customer-facing copy. */
  label: ReactNode;
  /** The button's words while counting down. The component supplies `secondsRemaining`; the
   *  caller supplies the sentence around it. This is the escape that keeps copy with the caller:
   *  the component must never interpolate the number into a sentence it wrote itself. */
  countdownLabel: (secondsRemaining: number) => ReactNode;
  /** Seconds the control stays disabled for, from mount and again after every press. Defaults to
   *  DEFAULT_THROTTLE_SECONDS; see that constant's doc comment for where 60 comes from. Read once,
   *  at mount, to set the first deadline: a prop change mid-countdown does not retime the
   *  in-progress countdown, only the next one a press starts. */
  throttleSeconds?: number;
  /** Disables the control while a resend request is in flight, independently of the countdown. */
  isResending?: boolean;
  /** An additional externally-owned condition that must also be false for the control to be
   *  enabled - independent of the countdown and `isResending`. Added for callers that gate the
   *  press on something of their own (e.g. a field the customer must fill in first): the control
   *  is enabled only once the countdown has expired, `isResending` is false, AND this is false.
   *  Optional and defaults to false, so every existing caller is unaffected. */
  isDisabled?: boolean;
  /** Called every time the countdown itself - not `isResending`, not `isDisabled` - crosses from
   *  running to expired: once the first time, and again after every later press whose own
   *  countdown also runs out. Never called while still counting down, and a press does not call
   *  it directly - only that press's countdown later expiring does. Lets a caller synchronise
   *  other UI to the moment a press first becomes real (e.g. revealing a field, or clearing one
   *  that could only ever have held something valid for the window that just ended) without
   *  polling this control's timer or running a second one. */
  onCountdownExpire?: () => void;
  'data-testid'?: string;
};

/**
 * A presentational "resend" control rendered as a single MUI `Button` in every state: disabled
 * from mount until `throttleSeconds` elapses, then enabled, showing `label`. All of the timing
 * behaviour lives in `useThrottledResend`; this component is a thin, unchanged-since-before-the-
 * reshape rendering of that hook for callers whose board draws the resend action as a real button
 * throughout (see docs/technical-debt.md TD-039, and useThrottledResend's own doc comment, for
 * the behavioural detail this component inherits unchanged).
 *
 * Screens whose board draws the resend action differently per state (a quiet line of text while
 * counting down, an inline pressable word once ready, or a button only in one particular state)
 * do not use this component: they call `useThrottledResend` directly and render their own markup
 * around it. See LinkSentResend.tsx and LinkSentSignInResend.tsx for two such compositions.
 */
export function ThrottledResend({
  onResend,
  label,
  countdownLabel,
  throttleSeconds,
  isResending,
  isDisabled,
  onCountdownExpire,
  'data-testid': dataTestId,
}: Readonly<ThrottledResendProps>): ReactElement {
  const { isCountingDown, secondsRemaining, disabled, handleResend } = useThrottledResend({
    onResend,
    throttleSeconds,
    isResending,
    isDisabled,
    onCountdownExpire,
  });

  return (
    <Button
      type="button"
      variant="text"
      disabled={disabled}
      onClick={handleResend}
      data-testid={dataTestId}
    >
      {isCountingDown ? countdownLabel(secondsRemaining) : label}
    </Button>
  );
}
